import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

/**
 * Lightweight starfield. Points-based, no per-frame buffer updates,
 * only a subtle rotation of the parent group.
 */
export function Particles({ count = 1400 }) {
    const ref = useRef();

    const { positions, colors } = useMemo(() => {
        const pos = new Float32Array(count * 3);
        const col = new Float32Array(count * 3);
        const yellow = new THREE.Color("#D69331");
        const white = new THREE.Color("#ffffff");
        for (let i = 0; i < count; i++) {
            const r = 6 + Math.random() * 18;
            const phi = Math.acos(2 * Math.random() - 1);
            const theta = Math.random() * Math.PI * 2;
            pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
            pos[i * 3 + 1] = r * Math.cos(phi) * 0.7;
            pos[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
            const c = Math.random() < 0.07 ? yellow : white;
            col[i * 3] = c.r;
            col[i * 3 + 1] = c.g;
            col[i * 3 + 2] = c.b;
        }
        return { positions: pos, colors: col };
    }, [count]);

    useFrame((state, delta) => {
        if (!ref.current) return;
        ref.current.rotation.y += delta * 0.02;
    });

    return (
        <points ref={ref}>
            <bufferGeometry>
                <bufferAttribute
                    attach="attributes-position"
                    args={[positions, 3]}
                />
                <bufferAttribute
                    attach="attributes-color"
                    args={[colors, 3]}
                />
            </bufferGeometry>
            <pointsMaterial
                size={0.025}
                vertexColors
                sizeAttenuation
                transparent
                opacity={0.85}
                depthWrite={false}
            />
        </points>
    );
}
